/* eslint-disable react/self-closing-comp */
import dynamic from 'next/dynamic';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  AVAILABLE_LOCALES,
  LOCALES,
  OMITTED_EDITORIAL_LOCALES,
} from 'localization/constants/configEnums';
import { AnimatedSearchSuggestions } from 'search/components/search/AnimatedSearchSuggestions';
import { Banner } from 'banner/components/Banner';
import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { DESKTOP_BANNER_HEIGHT, MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import {
  DESKTOP_HEADER_HEIGHT,
  DESKTOP_HIDE_HEADER_HEIGHT,
  NEW_NAV_MOBILE_HEADER_HEIGHT,
  NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT,
  NAV_PDP_MOBILE_HEADER_HEIGHT,
  DEFAULT_ITEMS_COUNT,
} from 'layout/header/enums/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { GoatLogo } from 'shared/icons/GoatLogo';
import { HeaderNavigationDesktop } from 'layout/header/components/HeaderNavigationDesktop';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { KebabMenu } from 'shared/components/KebabMenu';
import { MobileNavigationPanel } from 'layout/header/components/MobileNavigationPanel';
import { requestService } from 'shared/utils/services/requestService';
import { SearchPanelMobile } from 'search/components/search/SearchPanelMobile';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import {
  setIsMenuVisible,
  setMultipleLayoutOptions,
  setIsMobileSearchOpen,
} from 'store/slice/pageLayoutSlice';
import { SHOWED_COUNTRY_TOASTER } from 'search/enums/constants';
import { SizePreferencesModalProvider } from 'sizePreferences/providers/SizePreferencesModalProvider';
import { sortAndParseHeaderBreadcrumbs } from 'layout/header/utils/sortAndParseHeaderBreadcrumbs';
import { t } from 'localization/utils/t';
import { toggleVisible } from 'styles/theme/animation';
import { UnorderedList } from 'shared/components/UnorderedList';
import { useCountriesQuery } from 'store/api/countries';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useLocation } from 'shared/hooks/useLocation';
import { useSitePreferencesModal } from 'shared/hooks/useSitePreferencesModal';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { variableFontRegular } from 'styles/theme/text';
import { getListingsCount } from '../services/headerApi';

const TP = 'layout.header.components.Header';

const LogInCreateAccountModal = dynamic(
  () => import('account/components/LogInCreateAccountModal'),
  { ssr: false },
);

const ForgotPasswordModal = dynamic(() => import('account/components/ForgotPasswordModal'), {
  ssr: false,
});

const ResetPasswordModal = dynamic(() => import('account/components/ResetPasswordModal'), {
  ssr: false,
});

const SearchBarDesktop = dynamic(
  () => import('search/components/search/SearchBarDesktop').then((mod) => mod.SearchBarDesktop),
  {
    ssr: false,
  },
);

const Account = dynamic(
  () => import('layout/header/components/Account').then((mod) => mod.Account),
  {
    ssr: false,
  },
);

const MobilePdpHeader = dynamic(() => import('layout/header/components/MobilePdpHeader'), {
  ssr: true,
});

const Header = ({
  isInApp = false,
  isMobileUserAgent,
}: {
  isInApp?: boolean;
  isMobileUserAgent?: boolean;
}) => {
  const storeDispatch = useStoreDispatch();
  const { isHeaderVisible, isMobileSearchOpen, isMenuVisible, menuNavigation } = useStoreSelector(
    (state) => state.pageLayout,
  );
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const isMounted = useIsMounted();
  const headerLinks: ICMSBreadcrumb[] = sortAndParseHeaderBreadcrumbs(
    menuNavigation?.navigation?.children,
    BREADCRUMBS.NAVIGATION,
  );
  const calendarLinks: ICMSBreadcrumb[] = sortAndParseHeaderBreadcrumbs(
    menuNavigation?.calendar?.children,
    BREADCRUMBS.CALENDAR,
  );
  const showBlackFridayLink = useFeatureFlag(FeatureFlags.WEB_ENABLE_BLACK_FRIDAY_IN_NAV);
  const isBannerVisible = useIsBannerVisible();

  /*
   * To keep snapshots consistent, adding this state so that
   * if cypress is running we display the same amount of inventory to
   * snapshots consistent
   */
  const [isCypress, setIsCypress] = useState(false);
  const [numOfListings, setNumOfListings] = useState(DEFAULT_ITEMS_COUNT);

  const { isDesktop, isMobileOrTablet } = useWindowSize();
  const { modalContext } = useStoreSelector((state) => state.accountModal);
  const mobileNavPanelBodyRef = useRef<HTMLDivElement>();
  const { openSitePreferencesModal } = useSitePreferencesModal();
  const { selectedCountryCode } = useLocation();
  const { data } = useCountriesQuery();
  const countries = data?.countries || [];
  const selectedCountry = countries?.find((country) => country?.isoCode === selectedCountryCode);
  const { name } = selectedCountry || {};

  const { locale, pathname } = useRouter();
  const [showHeaderToaster, setShowHeaderToaster] = useState(false);
  const [userClicked, setUserClicked] = useState(false);

  const pdpPaths = new Set(['/sneakers/[productTemplateSlug]', '/apparel/[productTemplateSlug]']);
  const isPdpPath = pdpPaths.has(pathname);
  const [showMobilePdpHeader, setShowMobilePdpHeader] = useState(isMobileUserAgent && isPdpPath);

  const [{ data: suggestedSearches, error: suggestionErrors }, setSuggestedSearches] = useState<{
    data?: any[];
    error?: Error;
  }>({});

  const fetchListingCount = async () => {
    try {
      const listingsResponse = await getListingsCount();
      if (listingsResponse?.totalResults) {
        setNumOfListings(listingsResponse.totalResults);
      }
    } catch {
      // TODO_BUGSNAG this causes bugsnag overload from bots, commenting out for now
      // bugsnag(error, { context: 'Consumer Search request for listings failed' });
    }
  };

  const fetchSearchSuggestionsFeed = (slug: string) => {
    try {
      return requestService({
        url: `/web-api/v1/mosaic/articles/${slug}`,
        method: 'GET',
      }).then((response) => setSuggestedSearches({ data: response?.sections?.[0]?.subsections }));
    } catch (error) {
      if (error !== undefined) {
        bugsnag(error, { context: 'Error fetching CMS articles' });
      }
      return { error };
    }
  };

  useEffect(() => {
    const showedCountryToaster = sessionStorage.getItem(SHOWED_COUNTRY_TOASTER);
    const handleClicked = () => setUserClicked(true);
    document.addEventListener('click', handleClicked);

    if (locale !== LOCALES.enUS && !showedCountryToaster) {
      setShowHeaderToaster(true);
      sessionStorage.setItem(SHOWED_COUNTRY_TOASTER, 'true');
    }

    return () => {
      document.removeEventListener('click', handleClicked);
    };
  }, [locale]);

  useEffect(() => {
    if (isMounted()) {
      void fetchSearchSuggestionsFeed('search-suggestions-feed-web');
      void fetchListingCount();
    }
  }, []);

  const handleToasterClick = () => {
    openSitePreferencesModal();
    sendTrackingEvent('USER_REGION_TOASTER_TAP', {
      region: AVAILABLE_LOCALES[locale].country,
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
    setShowHeaderToaster(false);
  };

  const isLogInCreateAccountModalOpen =
    modalContext === 'logIn' || modalContext === 'createAccount';

  const isForgotPasswordModalOpen = modalContext === 'forgotPassword';

  const isResetPasswordModalOpen = modalContext === 'resetPassword';

  const openMobileNavMenu = () => {
    storeDispatch(setIsMenuVisible(true));
    sendTrackingEvent('MORE_MENU_VIEW', {
      service_name: isConsumerSearchEnabled ? 'consumer_search' : 'constructor',
    });
  };

  const closeMobileNavMenu = () => {
    storeDispatch(setIsMenuVisible(false));
  };

  const openMobileSearch = () => {
    storeDispatch(setIsMobileSearchOpen(true));
  };

  const closeMobileSearch = () => {
    storeDispatch(setIsMobileSearchOpen(false));
  };

  useEffect(() => {
    if (isMenuVisible) {
      closeMobileNavMenu();
    }

    // @ts-ignore cypress sets this window value
    if (window?.Cypress) {
      setIsCypress(true);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (isPdpPath && isMobileOrTablet) {
      setShowMobilePdpHeader(true);
    } else {
      setShowMobilePdpHeader(false);
    }
  }, [isMobileOrTablet, isPdpPath]);

  useEffect(() => {
    storeDispatch(
      setMultipleLayoutOptions({ isHeaderVisible: !isInApp, isFooterVisible: !isInApp, isInApp }),
    );
  }, [isInApp]);

  if (isInApp) {
    return null;
  }

  return (
    <SizePreferencesModalProvider>
      <Header.Container
        $isHeaderVisible={isHeaderVisible}
        $isBannerVisible={isBannerVisible}
        $showMobilePdpHeader={showMobilePdpHeader}
      >
        {isBannerVisible && <Banner />}
        <Header.Wrapper $showBF={showBlackFridayLink} $showMobilePdpHeader={showMobilePdpHeader}>
          {showMobilePdpHeader ? (
            <MobilePdpHeader
              showBlackFridayLink={showBlackFridayLink}
              openMobileNavMenu={openMobileNavMenu}
              openMobileSearch={openMobileSearch}
            />
          ) : (
            <>
              <Header.SearchDesktop>
                <SearchBarDesktop
                  isBannerVisible={isBannerVisible}
                  suggestedSearches={suggestedSearches}
                  error={suggestionErrors}
                />
              </Header.SearchDesktop>
              <Header.Navigation>
                <Header.NavigationList>
                  <HeaderNavigationDesktop
                    data-qa="header_nav_home"
                    href="/"
                    label={t(`${TP}.home`, 'Home')}
                  />
                  <HeaderNavigationDesktop
                    data-qa="header_nav_listings"
                    menuItems={headerLinks}
                    href="/search"
                    label={t(`${TP}.headerNavigationDesktopSearch`, {
                      defaultValue: 'Shop {numOfItems, plural, =1 {1 Item} other {# Items}}',
                      numOfItems: isCypress ? 255084 : numOfListings,
                    })}
                  />
                  {!OMITTED_EDITORIAL_LOCALES.includes(locale as LOCALES) && (
                    <HeaderNavigationDesktop
                      data-qa="header_nav_editorial"
                      href="/editorial"
                      label={t(`${TP}.headerNavigationDesktopEditorial`, 'Editorial')}
                    />
                  )}
                  <HeaderNavigationDesktop
                    data-qa="header_nav_calendar"
                    menuItems={calendarLinks}
                    href="/timeline"
                    label={t(`${TP}.headerNavigationDesktopCalendar`, 'Timeline')}
                  />
                </Header.NavigationList>
              </Header.Navigation>

              <Header.Logo>
                <Link
                  href="/"
                  passHref
                  prefetch={false}
                  aria-label={t(`${TP}.ariaLabelGoatLogo`, 'Goat Logo')}
                  data-qa="goat_logo"
                >
                  <Header.GoatLogo width={38} height={9} />
                </Link>
              </Header.Logo>

              {showBlackFridayLink && (
                <Header.Link
                  aria-label={t(`${TP}.ariaLabelBlackFriday`, 'Link to GOAT Black Friday Page')}
                  href="/blackfriday"
                >
                  {t(`${TP}.headerLinkBlackFriday2023`, 'BF23')}
                </Header.Link>
              )}
              <Header.SearchMobile
                $isBlackFridayLinkVisible={showBlackFridayLink}
                onClick={openMobileSearch}
                data-qa="mobile_search"
              >
                {t(`${TP}.search`, 'Search')}
                {!isCypress && <AnimatedSearchSuggestions suggestedSearches={suggestedSearches} />}
              </Header.SearchMobile>

              <Header.RightSideContainer>
                <Header.LeftInvisibleElementOfRightContainer />
                <Header.CenterRightSide>
                  <Header.HeaderToaster
                    data-qa="header_toaster"
                    onClick={handleToasterClick}
                    $isVisible={showHeaderToaster}
                    $userClicked={userClicked}
                  >
                    {t(`${TP}.shoppingRegion`, 'Your shopping region is')}
                    <Header.CountryToasterName>{name}</Header.CountryToasterName>
                  </Header.HeaderToaster>

                  <Header.BlackFridayLinkContainer>
                    {showBlackFridayLink && isDesktop && (
                      <HeaderNavigationDesktop
                        data-qa="header_nav_black_friday"
                        href="/blackfriday"
                        label={t(`${TP}.headerNavBlackFriday2023`, 'Black Friday 2023')}
                      />
                    )}
                  </Header.BlackFridayLinkContainer>
                </Header.CenterRightSide>
                <Header.AccountList>
                  <Header.Account />
                </Header.AccountList>
              </Header.RightSideContainer>
              <Header.Menu onClick={openMobileNavMenu} data-qa="nav_menu">
                <Header.KebabMenu />
              </Header.Menu>
            </>
          )}

          <MobileNavigationPanel
            calendarLinks={calendarLinks}
            closeMobileNavMenu={closeMobileNavMenu}
            headerItemsCount={numOfListings}
            headerLinks={headerLinks}
            mobileNavPanelBodyRef={mobileNavPanelBodyRef}
          />
          {isMobileSearchOpen && (
            <SearchPanelMobile
              closeSearch={closeMobileSearch}
              suggestedSearches={suggestedSearches}
              error={suggestionErrors}
            />
          )}
          {isLogInCreateAccountModalOpen && <LogInCreateAccountModal />}
          {isForgotPasswordModalOpen && <ForgotPasswordModal isOpen={isForgotPasswordModalOpen} />}
          {isResetPasswordModalOpen && <ResetPasswordModal isOpen={isResetPasswordModalOpen} />}
        </Header.Wrapper>
      </Header.Container>
      {/* Spacer is to maintain the header height since header has fixed position */}
      {isHeaderVisible && !showMobilePdpHeader && (
        <Header.Spacer $isBannerVisible={isBannerVisible} />
      )}
    </SizePreferencesModalProvider>
  );
};

export default Header;

const DESKTOP_HEADER_AND_BANNER_HEIGHT = `calc(${DESKTOP_HEADER_HEIGHT} + ${DESKTOP_BANNER_HEIGHT})`;
const NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${
  NEW_NAV_MOBILE_HEADER_HEIGHT as string
} + ${MOBILE_BANNER_HEIGHT})`;
const PDP_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${NAV_PDP_MOBILE_HEADER_HEIGHT} + ${MOBILE_BANNER_HEIGHT})`;

Header.Container = styled.header<{
  $isHeaderVisible: boolean;
  $isBannerVisible: boolean;
  $showMobilePdpHeader?: boolean;
}>`
  position: fixed;
  left: 0;
  right: 0;
  top: ${({ $isHeaderVisible, $isBannerVisible, $showMobilePdpHeader }) => {
    if ($isBannerVisible) {
      return $isHeaderVisible || $showMobilePdpHeader
        ? `0`
        : `calc(${NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT} - ${MOBILE_BANNER_HEIGHT})`;
    }
    return $isHeaderVisible || $showMobilePdpHeader ? `0` : NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT;
  }};
  transition: all 0.3s ease-in;
  height: ${({ $isBannerVisible, $showMobilePdpHeader }) => {
    if ($isBannerVisible) {
      return $showMobilePdpHeader
        ? PDP_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT
        : NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT;
    }
    return $showMobilePdpHeader ? NAV_PDP_MOBILE_HEADER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT;
  }};

  background: ${({ theme }) => theme.navigation.headerBackgroundColor};
  ${({ $showMobilePdpHeader, theme }) =>
    !$showMobilePdpHeader && `border-bottom: 1px solid ${theme.navigation.mainBorderColor};`}

  z-index: ${({ theme }) => theme.zIndex.nav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    top: ${({ $isHeaderVisible, $isBannerVisible, $showMobilePdpHeader }) => {
      if ($isBannerVisible) {
        return $isHeaderVisible || $showMobilePdpHeader
          ? `0`
          : `calc(${NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT} - ${MOBILE_BANNER_HEIGHT})`;
      }
      return $isHeaderVisible || $showMobilePdpHeader ? `0` : NEW_NAV_MOBILE_HIDE_HEADER_HEIGHT;
    }};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    top: ${({ $isHeaderVisible, $isBannerVisible }) => {
      if ($isBannerVisible) {
        return $isHeaderVisible
          ? `0`
          : `calc(${DESKTOP_HIDE_HEADER_HEIGHT} - ${DESKTOP_BANNER_HEIGHT})`;
      }
      return $isHeaderVisible ? `0` : DESKTOP_HIDE_HEADER_HEIGHT;
    }};
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT};
  }

  [data-qa='header_nav_black_friday'] {
    text-decoration: underline !important;
  }
`;

Header.Wrapper = styled.header<{ $showBF?: boolean; $showMobilePdpHeader?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: space-between;

  ${variableFontRegular({ size: '11.5px' })};
  padding: ${({ $showMobilePdpHeader }) => ($showMobilePdpHeader ? `6px;` : `16px 3px 12px 16px;`)};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    padding: 12px 32px 0;
    ${variableFontRegular({ size: '11.5px', height: '14px' })};
  }
`;

Header.Spacer = styled.header<{ $isBannerVisible: boolean }>`
  background: none;
  top: 0;
  position: sticky;
  height: ${({ $isBannerVisible }) => {
    return $isBannerVisible
      ? NEW_NAV_MOBILE_HEADER_AND_BANNER_HEIGHT
      : NEW_NAV_MOBILE_HEADER_HEIGHT;
  }};
  z-index: ${({ theme }) => theme.zIndex.appLandingNav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    height: ${({ $isBannerVisible }) =>
      $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT};
  }
`;

Header.Menu = styled.div`
  display: flex;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.KebabMenu = styled(KebabMenu)`
  padding: 6px 14px;

  ${KebabMenu.Dot} {
    background-color: ${({ theme }) => theme.navigation.textColor};
  }
`;

Header.Logo = styled.div`
  height: 100%;
  text-align: center;
  display: flex;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
    padding-top: 11px;
    place-self: start;
  }
`;

Header.GoatLogo = styled(GoatLogo)`
  fill: ${({ theme }) => theme.navigation.textColor} !important;
`;

Header.SearchDesktop = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: block;
  }
`;

Header.Navigation = styled.nav`
  position: relative;
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: flex;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    justify-content: center;
    white-space: nowrap;
  }
`;

Header.NavigationList = styled(UnorderedList)`
  display: flex;
`;

Header.Link = styled.a`
  align-items: center;
  display: flex;
  flex-direction: column;
  font-size: 11px;
  justify-content: center;
  line-height: 13px;
  margin-left: 16px;
  text-decoration: underline !important;
  text-transform: uppercase;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.Account = styled(Account)`
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge - 1}px) {
    margin: 20px 20px 40px;
  }
`;

Header.AccountList = styled(UnorderedList)`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: block;

    [data-qa='account_header'] {
      margin-bottom: 1px;
    }
  }
`;

Header.SearchMobile = styled.button<{ $isBlackFridayLinkVisible: boolean }>`
  cursor: pointer;
  display: flex;
  min-width: ${({ $isBlackFridayLinkVisible }) =>
    $isBlackFridayLinkVisible ? `calc(100vw - 180px)` : `calc(100vw - 142px)`};

  &:hover {
    text-decoration: underline;
  }

  text-align: left;
  border: 1px solid ${({ theme }) => rgba(theme.navigation.textColor, 0.7)};
  margin-left: 16px;
  margin-right: 4px;
  padding: 8px 10px 5px;

  &:hover {
    text-decoration: none;
  }

  &:focus,
  &:active {
    border: 1px solid ${({ theme }) => rgba(theme.navigation.textColor, 0.7)};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileNavItem = styled.button`
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileNavLink = styled.a`
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none;
  }
`;

Header.MobileLinks = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 100%;
  min-width: 100vw;
`;

Header.RightSideContainer = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: none !important;
  }

  display: flex;
  justify-content: space-between;
  width: calc(
    50vw - 173px
  ); // 173 is the width the center of the right minus the width that is taken up by the middle unordered list and padding
`;

Header.LeftInvisibleElementOfRightContainer = styled.div``;

Header.CenterRightSide = styled.div`
  position: relative;
`;

Header.BlackFridayLinkContainer = styled.div`
  bottom: 0;
  left: 0;
  margin: auto;
  width: 109px;
  position: absolute;
  right: 0;
  top: 0;
`;

Header.HeaderToaster = styled.div<{
  $absolutePosition?: boolean;
  $isVisible: boolean;
  $userClicked?: boolean;
}>`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  cursor: pointer;
  margin: 6px 0px 9px 0px;
  flex-wrap: nowrap;
  font-size: 9px;
  line-height: 11px;
  padding: 4px;
  width: fit-content;
  position: relative;
  opacity: 0;
  display: none;
  visibility: hidden;
  white-space: nowrap;
  ${({ $isVisible }) =>
    $isVisible &&
    css`
      top: 6px;
      ${toggleVisible}
    `};
  ${({ $userClicked, $isVisible }) =>
    ($userClicked || !$isVisible) &&
    `transform: translateY(-100%);
    visibility: hidden;
    opacity: 0;
    transition: all 0.5s ease-out;
  `};
  animation: slideIn 0.5s ease-in;
  border: ${({ theme }) => theme.navigation.border};
  z-index: ${({ theme }) => theme.zIndex.nav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: flex;
    align-content: center;
    justify-content: center;
  }

  @keyframes slideIn {
    0% {
      transform: translateY(-100%);
    }
    100% {
      transform: translateY(0%);
    }
  }
`;

Header.CountryToasterName = styled.u`
  margin-left: 2px;
`;

Header.HeaderToasterAbsolute = styled.div<{
  $isVisible: boolean;
}>`
  animation: slideIn 0.5s ease-in;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  border: ${({ theme }) => theme.navigation.border};
  cursor: pointer;
  flex-wrap: nowrap;
  font-size: 9px;
  left: 0;
  line-height: 11px;
  margin: auto;
  padding: 4px;
  position: absolute;
  right: 0;
  opacity: 0;
  display: none;
  visibility: hidden;
  width: fit-content;
  white-space: nowrap;
  ${({ $isVisible }) =>
    $isVisible &&
    css`
      top: 6px;
      ${toggleVisible}
    `};
  ${({ $isVisible }) =>
    !$isVisible &&
    `top: -48px;
    visibility: hidden;
    opacity: 0;
  `};
  transition: all 0.5s ease-out;
  z-index: ${({ theme }) => theme.zIndex.nav};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumLarge}px) {
    display: flex;
    align-content: center;
    justify-content: center;
  }

  @keyframes slideIn {
    0% {
      top: -48px;
    }
    100% {
      top: 6px;
    }
  }
`;
